import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import {
  X, User, Building2, AlertTriangle, MapPin, Heart,
  Syringe, Pill, FileText, Paperclip, Search, Filter,
  Phone, Mail, Globe, Shield, Bed, ClipboardList,
  Stethoscope, Info, ChevronDown,
} from 'lucide-react';

/* ════════════════ MOCK DATA ════════════════ */
function getMockRecord(patient) {
  return {
    anagrafica: {
      eta: patient.eta || 83,
      sesso: patient.sesso === 'M' ? 'Maschio' : 'Femmina',
      dataNascita: patient.dataNascita || '23/07/1942',
      luogoNascita: 'Roma (RM)',
      nazionalita: 'Italiana',
      cf: patient.codiceFiscale || 'VRDNNA72L63A944C',
      email: 'anna.verdi@email.it',
      telefono: patient.telefono || '+39 06 5432198',
      cellulare: '+39 347 5432198',
      residenza: patient.residenza || patient.indirizzo || 'Via Tuscolana 320, 00181 Roma (RM)',
      domicilio: patient.indirizzo || 'Via Tuscolana 320, 00181 Roma (RM)',
      mmg: patient.medicoRichiedente || 'Dr. Roberto Neri',
      codiceSTP: 'N/A',
      asl: patient.distretto || 'ASL Roma 2 — Distretto 7',
    },
    ricoveri: {
      quesito: 'Scompenso cardiaco acuto in paziente con cardiopatia ischemica cronica post-IMA (2018). Dispnea ingravescente classe NYHA III-IV con edemi declivi bilaterali.',
      anamnesiFisiologica: 'Alvo regolare. Diuresi contratta nelle ultime 48h (~400 mL/die). Sonno disturbato da ortopnea. Appetito ridotto. Non fumatore. Ex consumo moderato di alcol (cessato 2020).',
      anamnesiPatologica: 'Cardiopatia ischemica cronica post-IMA (PTCA+stent DES su IVA, 2018). Diabete mellito tipo 2 in terapia orale (dal 2015). BPCO stadio II GOLD. Ipertensione arteriosa in terapia. Pregressa frattura femore sx (osteosintesi, 2020).',
      reparto: 'Medicina Interna — Sez. B',
      letto: '12-A',
      dataIngresso: '22/12/2025',
      dataDimissione: '28/12/2025',
      diagnosiPrincipale: 'I50.0 — Insufficienza cardiaca congestizia',
      diagnosiSecondaria: 'E11.9 — Diabete mellito tipo 2 senza complicanze',
      regime: 'Ordinario',
      provenienza: 'Pronto Soccorso',
    },
    territoriale: {
      ambulatoriale: [
        { data: '10/02/2026', prestazione: 'Visita cardiologica di controllo', struttura: 'Poliambulatorio ASL RM2', medico: 'Dr. Conti Paolo' },
        { data: '28/01/2026', prestazione: 'Prelievo ematico (emocromo, glicemia, creatinina)', struttura: 'Lab. Analisi Distretto 7', medico: 'Inf. Galli Marco' },
        { data: '15/01/2026', prestazione: 'ECG di controllo', struttura: 'Poliambulatorio ASL RM2', medico: 'Dr. Conti Paolo' },
        { data: '05/01/2026', prestazione: 'Visita diabetologica', struttura: 'Centro Diabetologia Tor Vergata', medico: 'Dr.ssa Ferri Giulia' },
      ],
      domiciliare: [
        { data: '12/02/2026', prestazione: 'Accesso infermieristico — Monitoraggio PV e medicazione', struttura: 'ADI ASL Roma 2', medico: 'Inf. Colombo Rosa' },
        { data: '10/02/2026', prestazione: 'Seduta fisioterapia — Rinforzo arti inferiori', struttura: 'ADI ASL Roma 2', medico: 'Ft. Galli Marco' },
        { data: '08/02/2026', prestazione: 'Accesso OSS — Igiene e supporto ADL', struttura: 'ADI ASL Roma 2', medico: 'OSS Ferretti Anna' },
      ],
      residenziale: [
        { data: '01/11/2025', prestazione: 'Ciclo riabilitativo post-frattura (30gg)', struttura: 'RSA Villa Iris — Roma', medico: 'Dr. Marchetti Luca' },
      ],
    },
    anamnesi: {
      remota: 'Cardiopatia ischemica cronica post-infarto miocardico acuto (IMA) nel 2018, trattato con PTCA e impianto di stent DES su arteria interventricolare anteriore (IVA). Diabete mellito tipo 2 diagnosticato nel 2015, attualmente in terapia con Metformina 850 mg x2/die. BPCO stadio II GOLD con riacutizzazione nel 2022 (ricovero 8 giorni). Ipertensione arteriosa in terapia combinata. Frattura femore sinistro nel 2020 trattata con osteosintesi. Appendicectomia in eta pediatrica.',
      familiare: 'Padre deceduto a 62 anni per infarto miocardico acuto. Madre ipertesa, deceduta a 85 anni per ictus cerebrale. Un fratello affetto da diabete mellito tipo 2. Una sorella con ipotiroidismo. Nessuna familiarita per neoplasie.',
      fisiologica: 'Nato a termine da parto eutocico. Sviluppo psicomotorio nella norma. Scolarita: diploma scuola media superiore. Occupazione: pensionato (ex impiegato comunale). Stato civile: vedovo/a dal 2021. Convive con la figlia. Alimentazione: dieta ipoglucidica e iposodica. Non fumatore. Ex bevitore moderato (cessato 2020). Allergie note: Penicillina, Aspirina.',
    },
    profiloSintetico: {
      allergieFarmaco: ['Penicillina — Orticaria e angioedema', 'Aspirina (ASA) — Broncospasmo'],
      allergieAmbientali: ['Acari della polvere — Rinite allergica'],
      intolleranze: ['Lattosio — Disturbi gastrointestinali', 'Crostacei — Prurito orale e nausea'],
      patologie: [
        { codice: 'I25.1', nome: 'Cardiopatia ischemica cronica' },
        { codice: 'E11.9', nome: 'Diabete mellito tipo 2' },
        { codice: 'J44.1', nome: 'BPCO con riacutizzazione' },
        { codice: 'I10', nome: 'Ipertensione arteriosa essenziale' },
        { codice: 'I48.0', nome: 'Fibrillazione atriale parossistica' },
      ],
      protesi: ['Stent coronarico DES su IVA (2018)', 'Placca e viti femore sx (2020)', 'Protesi dentaria parziale superiore'],
    },
    vaccinazioni: [
      { vaccino: 'Anti-influenzale (stagionale)', data: '15/10/2025', dose: 'Annuale', stato: 'Completata', erogatore: 'MMG — Dr. Neri' },
      { vaccino: 'Anti-pneumococcica (PCV13)', data: '20/03/2024', dose: 'Singola', stato: 'Completata', erogatore: 'ASL Roma 2' },
      { vaccino: 'Anti-SARS-CoV-2 (booster)', data: '10/11/2023', dose: '4a dose', stato: 'Completata', erogatore: 'Hub Vaccinale Fiumicino' },
      { vaccino: 'Anti-Herpes Zoster (Shingrix)', data: '—', dose: '1a dose', stato: 'Raccomandata', erogatore: '—' },
      { vaccino: 'Anti-tetanica (richiamo)', data: '05/06/2019', dose: 'Richiamo', stato: 'Completata', erogatore: 'Pronto Soccorso' },
    ],
    terapie: [
      { aic: '023456012', nome: 'Furosemide 25 mg', posologia: '1 cp/die — mattino', data: '22/12/2025', stato: 'Attiva' },
      { aic: '034567023', nome: 'Ramipril 5 mg', posologia: '1 cp/die', data: '22/12/2025', stato: 'Attiva' },
      { aic: '045678034', nome: 'Bisoprololo 2.5 mg', posologia: '1 cp/die', data: '22/12/2025', stato: 'Attiva' },
      { aic: '056789045', nome: 'Metformina 850 mg', posologia: '2 cp/die (colazione+cena)', data: '15/03/2015', stato: 'Attiva' },
      { aic: '067890056', nome: 'Enoxaparina 4000 UI', posologia: '1 fl sc/die', data: '22/12/2025', stato: 'Attiva' },
      { aic: '078901067', nome: 'Paracetamolo 1000 mg', posologia: 'Al bisogno (max 3/die)', data: '22/12/2025', stato: 'Sospesa' },
    ],
    consensi: [
      { tipo: 'Consenso Informato ADI', data: '15/02/2026', firmato: true, formato: 'PDF', allegato: true },
      { tipo: 'Privacy — Trattamento Dati Sanitari', data: '15/02/2026', firmato: true, formato: 'PDF', allegato: true },
      { tipo: 'Consenso Telemedicina', data: '15/02/2026', firmato: true, formato: 'PDF', allegato: false },
      { tipo: 'Dichiarazione Anticipata Trattamento (DAT)', data: '—', firmato: false, formato: '—', allegato: false },
      { tipo: 'Scheda Dimissione Ospedaliera (SDO)', data: '28/12/2025', firmato: true, formato: 'PDF', allegato: true },
      { tipo: 'Lettera Dimissione Protetta', data: '28/12/2025', firmato: true, formato: 'PDF', allegato: true },
    ],
  };
}

/* ════════════ SHARED COMPONENTS ════════════ */
const SectionCard = ({ title, children, className = '' }) => (
  <div className={`rounded-xl bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700 p-5 ${className}`}>
    {title && <p className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider mb-4">{title}</p>}
    {children}
  </div>
);

const DataRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-700/40 last:border-b-0">
    <span className="text-[11px] text-slate-500 dark:text-slate-400">{label}</span>
    <span className="text-[11px] text-slate-900 dark:text-slate-200 font-medium text-right max-w-[60%]">{value}</span>
  </div>
);

const SearchBar = ({ placeholder = 'Cerca...' }) => (
  <div className="flex items-center gap-3">
    <div className="flex-1 flex items-center gap-2 h-9 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3">
      <Search className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
      <input type="text" placeholder={placeholder} className="bg-transparent text-xs text-slate-900 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none w-full" />
    </div>
    <button className="h-9 px-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-1.5 transition-colors">
      <Filter className="w-3.5 h-3.5" /> Filtra
    </button>
  </div>
);

const SegmentedControl = ({ options, active, onSelect }) => (
  <div className="flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700/40 w-fit">
    {options.map((opt) => (
      <button
        key={opt.key}
        onClick={() => onSelect(opt.key)}
        className={`px-4 py-2 rounded-md text-[11px] font-semibold transition-all ${
          active === opt.key
            ? 'bg-cyan-500 text-white shadow-sm shadow-cyan-500/30'
            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
        }`}
        data-testid={`seg-${opt.key}`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const DataTable = ({ columns, rows }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
    <table className="w-full text-left">
      <thead>
        <tr className="bg-slate-50 dark:bg-slate-800/80">
          {columns.map((col) => (
            <th key={col} className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-t border-slate-200 dark:border-slate-700/40 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-[11px] text-slate-700 dark:text-slate-300">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ════════════ TAB 1: ANAGRAFICA ════════════ */
function TabAnagrafica({ data }) {
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="content-anagrafica">
      <SectionCard title="Informazioni Generali">
        <DataRow label="Eta" value={`${data.eta} anni`} />
        <DataRow label="Sesso" value={data.sesso} />
        <DataRow label="Data di Nascita" value={data.dataNascita} />
        <DataRow label="Luogo di Nascita" value={data.luogoNascita} />
        <DataRow label="Nazionalita" value={data.nazionalita} />
        <DataRow label="Codice Fiscale" value={<span className="font-mono text-[10px]">{data.cf}</span>} />
      </SectionCard>
      <SectionCard title="Informazioni di Contatto">
        <DataRow label="Email" value={data.email} />
        <DataRow label="Telefono" value={data.telefono} />
        <DataRow label="Cellulare" value={data.cellulare} />
      </SectionCard>
      <SectionCard title="Residenza e Domicilio">
        <DataRow label="Residenza" value={data.residenza} />
        <DataRow label="Domicilio" value={data.domicilio} />
      </SectionCard>
      <SectionCard title="Assistenza Sanitaria">
        <DataRow label="Medico di Base (MMG)" value={data.mmg} />
        <DataRow label="ASL di Riferimento" value={data.asl} />
        <DataRow label="Codice STP" value={data.codiceSTP} />
      </SectionCard>
    </div>
  );
}

/* ════════════ TAB 2: RICOVERI ════════════ */
function TabRicoveri({ data }) {
  return (
    <div className="flex flex-col gap-4" data-testid="content-ricoveri">
      <div className="flex items-center gap-2 h-10 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3">
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
        <select className="bg-transparent text-xs text-slate-900 dark:text-slate-200 outline-none w-full cursor-pointer" data-testid="ricoveri-select">
          <option>I50.0 — Insufficienza cardiaca congestizia (22/12/2025)</option>
          <option>J44.1 — BPCO riacutizzata (03/05/2025)</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-4">
          <SectionCard>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider">Quesito Clinico</p>
              <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[9px] font-semibold">Ricovero in corso</Badge>
            </div>
            <p className="text-[11px] text-slate-700 dark:text-slate-300 leading-relaxed">{data.quesito}</p>
          </SectionCard>
          <SectionCard title="Anamnesi Prossima">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Fisiologica</p>
                <p className="text-[11px] text-slate-700 dark:text-slate-300 leading-relaxed">{data.anamnesiFisiologica}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Patologica</p>
                <p className="text-[11px] text-slate-700 dark:text-slate-300 leading-relaxed">{data.anamnesiPatologica}</p>
              </div>
            </div>
          </SectionCard>
        </div>
        <SectionCard title="Informazioni di Ricovero" className="h-fit">
          <DataRow label="Reparto" value={data.reparto} />
          <DataRow label="Letto" value={data.letto} />
          <DataRow label="Data Ingresso" value={data.dataIngresso} />
          <DataRow label="Data Dimissione" value={data.dataDimissione} />
          <DataRow label="Regime" value={data.regime} />
          <DataRow label="Provenienza" value={data.provenienza} />
          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700/40">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Diagnosi ICD-10</p>
            <div className="flex flex-col gap-1.5">
              <Badge className="bg-red-500/10 text-red-400 border border-red-500/20 text-[10px] font-mono w-fit">{data.diagnosiPrincipale}</Badge>
              <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-mono w-fit">{data.diagnosiSecondaria}</Badge>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

/* ════════════ TAB 3: ASSISTENZA TERRITORIALE ════════════ */
function TabTerritoriale({ data }) {
  const [sub, setSub] = useState('ambulatoriale');
  const rows = (data[sub] || []).map((r) => [r.data, r.prestazione, r.struttura, r.medico]);
  return (
    <div className="flex flex-col gap-4" data-testid="content-territoriale">
      <SegmentedControl
        options={[
          { key: 'ambulatoriale', label: 'Ambulatoriale' },
          { key: 'domiciliare', label: 'Domiciliare' },
          { key: 'residenziale', label: 'Residenziale/Semiresidenziale' },
        ]}
        active={sub}
        onSelect={setSub}
      />
      <DataTable columns={['Data', 'Prestazione', 'Struttura', 'Medico/Operatore']} rows={rows} />
    </div>
  );
}

/* ════════════ TAB 4: ANAMNESI ════════════ */
function TabAnamnesi({ data }) {
  const [sub, setSub] = useState('remota');
  return (
    <div className="flex flex-col gap-4" data-testid="content-anamnesi">
      <SegmentedControl
        options={[
          { key: 'remota', label: 'Anamnesi patologica remota' },
          { key: 'familiare', label: 'Anamnesi familiare' },
          { key: 'fisiologica', label: 'Anamnesi fisiologica remota' },
        ]}
        active={sub}
        onSelect={setSub}
      />
      <SectionCard>
        <p className="text-[11px] text-slate-700 dark:text-slate-300 leading-[1.8] whitespace-pre-line">{data[sub]}</p>
      </SectionCard>
    </div>
  );
}

/* ════════════ TAB 5: PROFILO SINTETICO ════════════ */
function TabProfilo({ data }) {
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="content-profilo">
      <SectionCard title="Allergie Note">
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Farmacologiche</p>
            <div className="flex flex-wrap gap-1.5">
              {data.allergieFarmaco.map((a) => (
                <span key={a} className="px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-[10px] text-red-400 font-medium">{a}</span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Ambientali</p>
            <div className="flex flex-wrap gap-1.5">
              {data.allergieAmbientali.map((a) => (
                <span key={a} className="px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] text-amber-400 font-medium">{a}</span>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>
      <SectionCard title="Intolleranze">
        <div className="flex flex-wrap gap-1.5">
          {data.intolleranze.map((i) => (
            <span key={i} className="px-2.5 py-1 rounded-full bg-violet-500/10 border border-violet-500/30 text-[10px] text-violet-400 font-medium">{i}</span>
          ))}
        </div>
      </SectionCard>
      <SectionCard title="Patologie Croniche">
        <div className="flex flex-col gap-2">
          {data.patologie.map((p) => (
            <div key={p.codice} className="flex items-center gap-2">
              <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/25 text-[9px] font-mono flex-shrink-0">[{p.codice}]</Badge>
              <span className="text-[11px] text-slate-700 dark:text-slate-300">{p.nome}</span>
            </div>
          ))}
        </div>
      </SectionCard>
      <SectionCard title="Protesi e Dispositivi">
        <div className="flex flex-col gap-2">
          {data.protesi.map((p, i) => (
            <div key={i} className="flex items-start gap-2 text-[11px] text-slate-700 dark:text-slate-300">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0 mt-1.5" />
              {p}
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

/* ════════════ TAB 6: VACCINAZIONI ════════════ */
function TabVaccinazioni({ data }) {
  const rows = data.map((v) => [
    v.vaccino,
    v.data,
    v.dose,
    <Badge key={v.vaccino} className={`text-[9px] font-semibold border-0 ${
      v.stato === 'Completata' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
    }`}>{v.stato}</Badge>,
    v.erogatore,
  ]);
  return (
    <div className="flex flex-col gap-4" data-testid="content-vaccinazioni">
      <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">Registro vaccinale del paziente. Sono elencate le somministrazioni effettuate e le vaccinazioni raccomandate non ancora eseguite.</p>
      <SearchBar placeholder="Cerca vaccino..." />
      <DataTable columns={['Vaccino', 'Data Somm.', 'Dose', 'Stato', 'Erogatore']} rows={rows} />
    </div>
  );
}

/* ════════════ TAB 7: TERAPIE ════════════ */
function TabTerapie({ data }) {
  const rows = data.map((t) => [
    <span key={t.aic} className="font-mono text-[10px] text-slate-400 dark:text-slate-500">{t.aic}</span>,
    <span key={t.nome} className="font-semibold text-slate-900 dark:text-slate-200">{t.nome}</span>,
    t.posologia,
    t.data,
    <Badge key={`s-${t.aic}`} className={`text-[9px] font-semibold border-0 ${
      t.stato === 'Attiva' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-600/40 text-slate-400'
    }`}>{t.stato}</Badge>,
  ]);
  return (
    <div className="flex flex-col gap-4" data-testid="content-terapie">
      <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-blue-800 dark:text-blue-200 leading-relaxed">I dati terapeutici provengono dal Fascicolo Sanitario Elettronico (FSE) e potrebbero non includere prescrizioni esterne al SSR.</p>
      </div>
      <SearchBar placeholder="Cerca farmaco o AIC..." />
      <DataTable columns={['AIC', 'Terapia / Farmaco', 'Posologia', 'Data Somm.', 'Stato']} rows={rows} />
    </div>
  );
}

/* ════════════ TAB 8: CONSENSI ════════════ */
function TabConsensi({ data }) {
  const rows = data.map((c) => [
    <span key={c.tipo} className="font-medium text-slate-900 dark:text-slate-200">{c.tipo}</span>,
    c.data,
    c.firmato
      ? <Badge key={`f-${c.tipo}`} className="bg-emerald-500/15 text-emerald-400 border-0 text-[9px]">Si</Badge>
      : <Badge key={`f-${c.tipo}`} className="bg-red-500/15 text-red-400 border-0 text-[9px]">No</Badge>,
    c.formato !== '—' ? <Badge key={`fmt-${c.tipo}`} className="bg-slate-600/40 text-slate-300 border-0 text-[9px] font-mono">{c.formato}</Badge> : '—',
    c.allegato
      ? <Paperclip key={`a-${c.tipo}`} className="w-3.5 h-3.5 text-cyan-400" />
      : <span key={`a-${c.tipo}`} className="text-slate-600">—</span>,
  ]);
  return (
    <div className="flex flex-col gap-4" data-testid="content-consensi">
      <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">Elenco dei consensi, dichiarazioni e documenti clinici associati al paziente.</p>
      <SearchBar placeholder="Cerca documento..." />
      <DataTable columns={['Tipo Documento', 'Data', 'Firmato', 'Formato', 'Allegati']} rows={rows} />
    </div>
  );
}

/* ════════════ TABS CONFIG ════════════ */
const TABS = [
  { key: 'anagrafica', label: 'Anagrafica Paziente', icon: User },
  { key: 'ricoveri', label: 'Ricoveri', icon: Building2 },
  { key: 'territoriale', label: 'Assistenza Territoriale', icon: MapPin },
  { key: 'anamnesi', label: 'Anamnesi', icon: ClipboardList },
  { key: 'profilo', label: 'Profilo Sintetico', icon: AlertTriangle, warning: true },
  { key: 'vaccinazioni', label: 'Vaccinazioni', icon: Syringe },
  { key: 'terapie', label: 'Terapie e Prescrizioni', icon: Pill },
  { key: 'consensi', label: 'Consensi e Documentazione', icon: FileText },
];

/* ════════════════════════════════════════════
   MAIN EXPORT
   ════════════════════════════════════════════ */
export default function PatientRecordModal({ open, onClose, patient }) {
  const [activeTab, setActiveTab] = useState('anagrafica');

  if (!open || !patient) return null;

  const rec = getMockRecord(patient);

  const renderContent = () => {
    switch (activeTab) {
      case 'anagrafica': return <TabAnagrafica data={rec.anagrafica} />;
      case 'ricoveri': return <TabRicoveri data={rec.ricoveri} />;
      case 'territoriale': return <TabTerritoriale data={rec.territoriale} />;
      case 'anamnesi': return <TabAnamnesi data={rec.anamnesi} />;
      case 'profilo': return <TabProfilo data={rec.profiloSintetico} />;
      case 'vaccinazioni': return <TabVaccinazioni data={rec.vaccinazioni} />;
      case 'terapie': return <TabTerapie data={rec.terapie} />;
      case 'consensi': return <TabConsensi data={rec.consensi} />;
      default: return null;
    }
  };

  const isMale = patient.sesso === 'M';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center"
        data-testid="patient-record-modal"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

        {/* Panel */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="relative w-[92vw] max-w-7xl h-[88vh] bg-slate-50 dark:bg-slate-950 rounded-2xl shadow-2xl shadow-black/20 dark:shadow-black/50 flex flex-col overflow-hidden z-10 border border-slate-200 dark:border-slate-700"
          data-testid="patient-record-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isMale ? 'bg-blue-100 dark:bg-blue-500/15' : 'bg-pink-100 dark:bg-pink-500/15'}`}>
                <User className={`w-5 h-5 ${isMale ? 'text-blue-500 dark:text-blue-400' : 'text-pink-500 dark:text-pink-400'}`} />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{patient.cognome} {patient.nome}</h2>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">{patient.eta} anni</span>
                  <span className="text-slate-300 dark:text-slate-600">|</span>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500 font-mono">{patient.codiceFiscale}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden flex">
            {/* Sidebar */}
            <div className="w-64 flex-shrink-0 bg-white dark:bg-[#151f32] border-r border-slate-200 dark:border-slate-800 flex flex-col justify-between p-3 overflow-y-auto" data-testid="patient-record-sidebar">
              <div className="flex flex-col gap-0.5">
                {TABS.map(({ key, label, icon: Icon, warning }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-3 w-full px-3.5 py-2.5 rounded-lg text-left transition-all ${
                      activeTab === key
                        ? 'bg-cyan-50 dark:bg-cyan-500/15 text-cyan-600 dark:text-cyan-400'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200'
                    }`}
                    data-testid={`tab-${key}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${activeTab === key ? 'text-cyan-500 dark:text-cyan-400' : 'text-slate-400 dark:text-slate-500'}`} />
                    <span className="text-[11px] font-medium">{label}</span>
                    {warning && <AlertTriangle className="w-3 h-3 text-amber-400 ml-auto flex-shrink-0" />}
                  </button>
                ))}
              </div>

              {/* Close Button — bottom right of sidebar */}
              <button
                onClick={onClose}
                className="mt-4 w-full py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm shadow-cyan-500/30"
                data-testid="patient-record-close"
              >
                <X className="w-3.5 h-3.5" /> Chiudi Scheda
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-y-auto bg-slate-50 dark:bg-slate-950" data-testid="patient-record-content">
              {renderContent()}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
